import { motion } from "framer-motion";
import { Camera, Share2, Globe, Megaphone, ArrowUpRight } from "lucide-react";
import SplitReveal from "@/components/anim/SplitReveal";

/**
 * Leistungen. Orbit Visualisierung.  Im Zentrum: "Deine Marke".
 * Drum herum kreisen die drei Kern Disziplinen (Videoproduktion,
 * Social Media, Webseite) auf einer gemeinsamen Umlaufbahn.  Der
 * äußere Container rotiert langsam, jede Karte counter rotiert damit
 * die Beschriftung lesbar bleibt.  Jede Karte ist klickbar und
 * springt in die passende Deep Section.
 *
 * Auf Mobile fällt die Visualisierung in ein simples 3 Karten Stack
 * zurück, reine CSS Fallback ohne extra JS.
 */

const DISCIPLINES = [
  {
    icon: Camera,
    label: "Medienproduktion",
    tag: "Hochwertige Foto und Videoproduktion für deine Marke.",
    href: "#detail-video",
    angle: -90, // top
  },
  {
    icon: Share2,
    label: "Social Media",
    tag: "Content & Reels",
    href: "#detail-social-media",
    angle: 0, // right
  },
  {
    icon: Megaphone,
    label: "Marketing",
    tag: "Zielgruppe → Botschaft → Sichtbarkeit → Anfrage",
    href: "#detail-marketing",
    angle: 90, // bottom
  },
  {
    icon: Globe,
    label: "Webseite",
    tag: "SEO & Conversion",
    href: "#detail-website",
    angle: 180, // left
  },
];

const ORBIT_DURATION = 45; // Sekunden pro Umdrehung

export default function Leistungen() {
  return (
    <section
      id="leistungen"
      data-testid="leistungen-section"
      aria-labelledby="leistungen-heading"
      className="relative bg-ink py-20 lg:py-28 overflow-hidden border-t border-white/5"
    >
      {/* Ambient cyan glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(900px circle at 50% 50%, rgba(63,211,219,0.08), transparent 60%)",
        }}
      />

      <div className="mx-auto max-w-[1400px] px-6 lg:px-12 relative">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15%" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center justify-center gap-3 text-xs uppercase tracking-[0.25em] text-signal mb-6"
          >
            <span className="h-px w-10 bg-signal" />
            Leistungen
            <span className="h-px w-10 bg-signal" />
          </motion.div>

          <SplitReveal
            as="h2"
            id="leistungen-heading"
            lines={["Deine Marke im Mittelpunkt.", "Wir bauen den Rest."]}
            className="font-display font-black tracking-tighter text-bone leading-[1.0]"
            duration={0.9}
            stagger={0.05}
          />
          <style>{`#leistungen h2 { font-size: clamp(1.75rem, 3.8vw, 2.75rem); }`}</style>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-8 text-sm lg:text-base text-bone/65 leading-relaxed"
          >
            Medienproduktion, Social Media, Marketing und Webseite. Vier
            Disziplinen, die in einem System zusammen wirken. Alles greift
            ineinander, alles zahlt auf deine Marke ein.
          </motion.p>
        </div>

        {/* Orbit, nur ab lg sichtbar */}
        <div className="hidden lg:block relative mx-auto mt-16" style={{ width: 620, height: 620 }}>
          {/* Statisches Ringgitter für Tiefe */}
          <div
            aria-hidden
            className="absolute inset-0 rounded-full"
            style={{
              border: "1px dashed rgba(255,255,255,0.08)",
            }}
          />
          <div
            aria-hidden
            className="absolute rounded-full"
            style={{
              inset: 80,
              border: "1px dashed rgba(63,211,219,0.18)",
            }}
          />

          {/* Zentrum: Deine Marke */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center text-center z-10">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="w-52 h-52 rounded-full flex flex-col items-center justify-center relative"
              style={{
                background:
                  "radial-gradient(circle at 50% 40%, rgba(63,211,219,0.18) 0%, rgba(30,31,36,0.9) 60%, rgba(30,31,36,1) 100%)",
                border: "1px solid rgba(63,211,219,0.4)",
                boxShadow:
                  "0 0 60px rgba(63,211,219,0.25), inset 0 0 40px rgba(63,211,219,0.12)",
              }}
            >
              <div className="text-[10px] uppercase tracking-[0.25em] text-signal mb-2">
                Im Zentrum
              </div>
              <div className="text-2xl font-display font-black text-bone leading-none">
                Deine Marke
              </div>
              <div className="mt-3 text-[11px] uppercase tracking-widest text-bone/50">
                Positionierung · CI
              </div>
            </motion.div>
          </div>

          {/* Orbit, rotierender Container mit den 3 Satelliten */}
          <div
            className="absolute inset-0"
            style={{
              animation: `cre8-orbit-spin ${ORBIT_DURATION}s linear infinite`,
              willChange: "transform",
            }}
          >
            {DISCIPLINES.map((d) => {
              const Icon = d.icon;
              return (
                <a
                  key={d.label}
                  href={d.href}
                  data-testid={`leistungen-${d.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="absolute top-1/2 left-1/2 group"
                  style={{
                    // Positioniert die Karte auf der Umlaufbahn
                    transform: `translate(-50%, -50%) rotate(${d.angle}deg) translateY(-260px) rotate(${-d.angle}deg)`,
                  }}
                >
                  {/* Counter Rotation damit Text lesbar bleibt */}
                  <div
                    style={{
                      animation: `cre8-orbit-counter ${ORBIT_DURATION}s linear infinite`,
                      willChange: "transform",
                    }}
                    className="w-52 rounded-2xl border border-white/10 bg-ink/90 backdrop-blur-sm p-5 hover:border-signal/50 hover:bg-white/[0.04] transition-[background-color,border-color] duration-500"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div
                        className="inline-flex items-center justify-center w-10 h-10 rounded-xl"
                        style={{
                          background: "rgba(63,211,219,0.1)",
                          border: "1px solid rgba(63,211,219,0.3)",
                          color: "#3FD3DB",
                        }}
                      >
                        <Icon size={18} strokeWidth={1.8} />
                      </div>
                      <ArrowUpRight
                        size={16}
                        className="text-bone/40 group-hover:text-signal transition-colors"
                      />
                    </div>
                    <div className="font-display font-bold text-bone text-base leading-tight">
                      {d.label}
                    </div>
                    <div className="text-xs text-bone/55 mt-1">{d.tag}</div>
                  </div>
                </a>
              );
            })}
          </div>

          {/* Verbindungslinien vom Zentrum zu den 3 Positionen (statisch, dezent) */}
          <svg
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            width="620"
            height="620"
            viewBox="0 0 620 620"
          >
            {DISCIPLINES.map((d) => {
              const rad = (d.angle * Math.PI) / 180;
              const x = 310 + Math.cos(rad) * 260;
              const y = 310 + Math.sin(rad) * 260;
              return (
                <line
                  key={d.label}
                  x1="310"
                  y1="310"
                  x2={x}
                  y2={y}
                  stroke="rgba(63,211,219,0.15)"
                  strokeWidth="1"
                  strokeDasharray="3 6"
                />
              );
            })}
          </svg>
        </div>

        {/* Mobile / Tablet Fallback. 3 stacked Karten */}
        <div className="lg:hidden mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {DISCIPLINES.map((d) => {
            const Icon = d.icon;
            return (
              <a
                key={d.label}
                href={d.href}
                data-testid={`leistungen-mobile-${d.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group block rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-signal/40 transition-[background-color,border-color] duration-500"
              >
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="inline-flex items-center justify-center w-10 h-10 rounded-xl"
                    style={{
                      background: "rgba(63,211,219,0.1)",
                      border: "1px solid rgba(63,211,219,0.3)",
                      color: "#3FD3DB",
                    }}
                  >
                    <Icon size={18} strokeWidth={1.8} />
                  </div>
                  <ArrowUpRight
                    size={16}
                    className="text-bone/40 group-hover:text-signal transition-colors"
                  />
                </div>
                <div className="font-display font-bold text-bone text-base leading-tight">
                  {d.label}
                </div>
                <div className="text-xs text-bone/55 mt-1">{d.tag}</div>
              </a>
            );
          })}
        </div>
      </div>

      {/* Keyframes für Orbit und Counter Rotation.  Wir nutzen CSS statt
          Framer Motion damit die Animation auf einem eigenen Compositor Layer
          läuft und weniger CPU frisst. */}
      <style>{`
        @keyframes cre8-orbit-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes cre8-orbit-counter {
          from { transform: rotate(0deg); }
          to   { transform: rotate(-360deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          #leistungen [style*="cre8-orbit"] { animation: none !important; }
        }
      `}</style>
    </section>
  );
}
